<template>
	<div
		v-show="showAuthPopup"
		ref="popupRef"
		class="
        absolute bottom-6 left-24 z-9999 min-h-[364px] w-[318px]
        rounded-[10px] bg-white p-8 pb-11 font-Inter shadow-auth-popup
    "
		:class="
			showPopupOpenAnimation ? 'animate-headShake' : ''
		"
	>
		<div
			class="flex w-full items-center"
			:class="view === VIEWS.default ? 'justify-end' : 'justify-between'"
		>
			<div
				v-if="view !== VIEWS.default"
				class="group flex cursor-pointer items-center gap-1 text-won-blue"
				@click="() => changeView(VIEWS.default)"
			>
				<ArrowLeftIcon
					class="size-4"
				/>
            
				<span class="text-[14px] leading-[100%] underline-offset-2 group-hover:underline">
					Back
				</span>
			</div>
        
        
			<XMarkIcon
				class="size-6 cursor-pointer"
				@click="authPopupStore.hideShowAuthPopup"
			/>
		</div>
    
		<div v-if="view === VIEWS.default">
			<div class="relative mt-6 text-center text-won-blue">
				<h2 class="text-[24px] leading-[30px] font-semibold">
					{{ popupText.title }}
				</h2>
            
				<p class="mt-2 text-[14px] leading-[20px]">
					{{ popupText.description }}
				</p>
			</div>
        
			<div class="mt-6 text-won-dark-blue">
				<button
					type="button"
					class="gap-2"
					:class="socialButtonStyle"
					@click="handleSocialLogin"
				>
					<GoogleIcon />
					Log in with Google
				</button>
    
				<button
					type="button"
					class="mt-4 gap-2"
					:class="socialButtonStyle"
					@click="() => changeView(VIEWS.login)"
				>
					<EnvelopeIcon class="size-4" />
					Log in with Email
				</button>
			</div>
        
			<div class="mt-6">
				<p
					class="mt-4 text-center text-base text-won-blue"
				>
					Don't have an account?
					<span
						class="mt-2 w-fit cursor-pointer text-won-green underline-offset-2 hover:underline"
						@click="() => changeView(VIEWS.register)"
					>
						Sign up
					</span>
				</p>
			</div>
		</div>
    
		<div v-if="view === VIEWS.login">
			<AuthPopupLogin
				:loading="loading"
				:errors="errors"
				@login="loginHandler"
			/>
		</div>

		<div v-if="view === VIEWS.register">
			<AuthPopupRegister
				:loading="loading"
				@register="registerHandler"
			/>
		</div>
	</div> 
</template>

<script setup>
import { ref, shallowRef, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { XMarkIcon, EnvelopeIcon, ArrowLeftIcon  } from '@heroicons/vue/24/outline';
import { socialButtonStyle } from '../../utils/styles';
import { formatErrors, popupwindow, validateEmail } from '../../utils/helpers';
import { redirectAfterLogin } from '../../utils/auth-redirect';
import { markJustRegistered } from '../../utils/just-registered';
import { POPUP_FEATURE_TEXT_MAP } from '../../constants/auto-popup';
import { setAuthState } from '../../composables/auth';
import { useClickOutsideIgnore } from '../../composables/useClickOutsideIgnore';

import { useAuthPopupStore } from '../../store/auth-popup.store';
import { useNotifications } from '../../store/notifications.store';

import AuthPopupRegister from './components/auth-popup-register.vue';
import AuthPopupLogin from './components/auth-popup-login.vue';

import GoogleIcon from '../../icons/google.vue';

const { authenticate, register, setUserData } = setAuthState();
const { addIgnoreEl } = useClickOutsideIgnore();

const authPopupStore = useAuthPopupStore();
const notificationsStore = useNotifications();
const { showAuthPopup, showPopupOpenAnimation, activeFeature } = storeToRefs(authPopupStore);

const VIEWS = {
	default: 'default',
	login: 'login',
	register: 'register',
};

const router = useRouter();
const route = useRoute();

const popupRef = ref(null);
const view = shallowRef(VIEWS.default);
const loading = shallowRef(false);
const errors = ref({});

const changeView = (newView) => {
	view.value = newView;
};

const loginHandler = async ({ email, password }) => {
	if (!email?.length || !password?.length) {
		notificationsStore.notify('Enter credentials!', 'warning');
		return;
	}

	loading.value = true;

	try {
		const response  = await authenticate({ email, password, modal_name: activeFeature.value });
        
		errors.value = {};
        
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({
			event: 'user_login',
			user_id: response?.id
		});

		redirectAfterLogin(router, authPopupStore);
	} catch (error) {
		if (error?.fieldErrors) {
			errors.value = formatErrors(error.fieldErrors);
		} else {
			notificationsStore.notify(error?.message ?? 'Invalid request!', 'error');
		}
	} finally {
		loading.value = false;
	}
};

const registerHandler = async ({ email, password, confirmPassword, first_name, last_name }) => {
	if (!email?.length) {
		notificationsStore.notify('Please enter valid email!', 'warning');
		return;
	}

	if (!validateEmail(email)) {
		notificationsStore.notify('Please enter valid email!', 'warning');
		return;
	}

	if (!password?.length) {
		notificationsStore.notify('Please enter valid password!', 'warning');
		return;
	}

	if (!confirmPassword?.length) {
		notificationsStore.notify('Please enter valid confirm password!', 'warning');
		return;
	}

	if (password !== confirmPassword) {
		notificationsStore.notify('Passwords don\'t match!', 'warning');
		return;
	}

	loading.value = true;

	register({
		email: email,
		password: password,
		password_confirmation: confirmPassword,
		first_name,
		last_name,
		modal_name: activeFeature.value
	})
		.then((res) => {
			notificationsStore.notify('Successfully registered!');
			errors.value = {};

			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({
				event: 'register_account',
				user_id: res?.id
			});

			// Survives the reload below, so the surface they registered from can tell this is a
			// brand-new account rather than a returning seeker signing in.
			markJustRegistered();

			router.go();
		})
		.catch((error) => {
			const errorsObj = error?.fieldErrors;

			if (errorsObj != null) {
				const subjects = Object.keys(errorsObj);

				subjects.forEach((subject) => {
					errorsObj[subject].forEach((err) => {
						notificationsStore.notify(`${String(subject).charAt(0).toUpperCase() + String(subject).slice(1)} - ${err}`, 'error');
					});
				});
			} else {
				notificationsStore.notify(error?.message ?? 'Registration failed, please try again!', 'error');
			}
		})
		.finally(() => {
			loading.value = false;
		});
};

const handleSocialLogin = () => {
	popupwindow('/google/login', 'Google Login', 500, 600);

	window.onmessage = function ({ data: { user, type, newly_registered_user } }) {
		if (type === 'social') {
			setUserData(user);

			window.dataLayer = window.dataLayer || [];
            
			if (newly_registered_user) {
				window.dataLayer.push({
					event: 'register_account',
					user_id: user?.id
				});

				markJustRegistered();
			}

			if (!newly_registered_user) {
				window.dataLayer.push({
					event: 'user_login',
					user_id: user?.id
				});
			}

			setTimeout(() => {
				redirectAfterLogin(router, authPopupStore);
			}, 300);
		}
	};
};

const popupText = computed(() => {
	return POPUP_FEATURE_TEXT_MAP[route.name] || POPUP_FEATURE_TEXT_MAP['resumes.edit'];
});

onMounted(() => {
	addIgnoreEl(popupRef);
});
</script>
